// Overrides the Sepiida Component default styles

export const palette = {
  black: '#292C39',
  dark: '#4D546A',
  white: '#ffffff',
  light: '#F6F9FB',
  softLight: '#E4ECF3',
  secondary: '#12e5e0',
  lightBorder: '#e4ecf3',
  warning: '#fcbd01',
  primary: '#1686FF',
  darkBorder: '#8E9BAA',
  softDark: '#7C8A9C',
  error: '#FF5A64',
  success: '#03c148',
};

const borderRadiusBase = 6;

const textBase = {
  fontFamily: "'Noto Sans', Tahoma, Geneva, Verdana, sans-serif",
};

export const dashboardTheme = {
  palette,
  body: {
    fontFamily: textBase.fontFamily,
    color: palette.dark,
    margin: 0,
    padding: 0,
  },
  button: {
    border: {
      radius: `${borderRadiusBase}px`,
    },
    variant: {
      active: {
        backgroundColor: palette.black,
        hoverColor: 'inherit',
        borderColor: 'transparent',
      },
      text: {
        focusBorderColor: 'transparent',
        focusBackgroundColor: 'transparent',
        hoverBackgroundColor: 'transparent',
        hoverBorderColor: 'transparent',
        color: palette.primary,
        hoverColor: palette.dark,
        focusColor: palette.dark,
      },
      contained: {
        backgroundColor: palette.black,
        borderColor: palette.black,
        borderRadius: `${borderRadiusBase}px`,
        hoverBackgroundColor: palette.primary,
        hoverBorderColor: palette.primary,
        focusBackgroundColor: palette.primary,
        focusBorderColor: palette.primary,
        color: palette.white,
        borderWidth: '2px',
        fontWeight: 'bold',
        disabled: {
          backgroundColor: palette.white,
          borderColor: palette.lightBorder,
        },
      },
      outlined: {
        focusBorderColor: palette.primary,
        focusBackgroundColor: 'transparent',
        hoverBackgroundColor: 'transparent',
        hoverBorderColor: palette.primary,
        borderColor: palette.black,
        color: palette.black,
        hoverColor: palette.primary,
        focusColor: palette.primary,
        borderRadius: `${borderRadiusBase}px`,
      },
      cancel: {
        color: palette.primary,
      },
    },
    spacing: {
      compact: {
        small: {
          padding: 0,
        },
      },
    },
  },

  checkbox: {
    color: {
      primary: palette.primary,
    },
    label: {
      color: palette.dark,
    },
  },

  datePicker: {
    dropdownContainerBorder: `1px solid ${palette.lightBorders}`,
    monthYearDropdown: {
      color: palette.dark,
      hoverBorder: `1px solid ${palette.primary}`,
      focusBorder: `1px solid ${palette.primary}`,
      activeBorder: `1px solid ${palette.primary}`,
    },
    calendar: {
      dayColor: palette.dark,
      dayHoverBorder: `1px solid ${palette.primary}`,
      dayFocusBorder: `1px solid ${palette.primary}`,
      dayActiveBorder: `1px solid ${palette.primary}`,
      currentDayColor: palette.dark,
      currentDayBorder: `1px solid ${palette.dark}`,
      selectedDayColor: palette.light,
      selectedDayBackgroundColor: palette.primary,
      monthColor: palette.dark,
      monthHoverBorder: `1px solid ${palette.primary}`,
      monthFocusBorder: `1px solid ${palette.primary}`,
      monthActiveBorder: `1px solid ${palette.primary}`,
      selectedMonthColor: palette.light,
      selectedMonthBackgroundColor: palette.primary,
      yearColor: palette.dark,
      yearHoverBorder: `1px solid ${palette.primary}`,
      yearFocusBorder: `1px solid ${palette.primary}`,
      yearActiveBorder: `1px solid ${palette.primary}`,
      selectedYearColor: palette.light,
      selectedYearBackgroundColor: palette.primary,
    },
    navButtonColor: palette.primary,
    navButtonFocusBorder: `1px solid ${palette.primary}`,
  },

  imageUpload: {
    backgroundColor: palette.softLight,
  },

  menu: {
    variant: {
      standalone: {
        borderRadius: `${borderRadiusBase}px`,
      },
    },
  },

  modal: {
    backgroundColor: palette.white,
    borderRadius: '12px',
    border: 'none',
    boxShadow: '0 16px 32px 0 #0000001A',
  },

  pagination: {
    pageButtonColor: palette.dark,
    pageButtonHoverBackgroundColor: palette.softLight,
    pageButtonFocusOutline: `1px solid ${palette.primary}`,
    currentPageButtonColor: palette.light,
    currentPageButtonBackgroundColor: palette.dark,
    enabledArrowButtonColor: palette.dark,
    enabledArrowButtonBackgroundColor: 'transparent',
    enabledArrowButtonFocusColor: palette.dark,
    enabledArrowButtonFocusOutline: `1px solid ${palette.primary}`,
    disabledArrowButtonColor: palette.softLight,
    disabledArrowButtonBackgroundColor: 'transparent',
  },

  radio: {
    backgroundColor: palette.light,
    checked: {
      borderColor: palette.primary,
    },
    unchecked: {
      borderColor: palette.darkBorder,
    },
  },

  slider: {
    backgroundColor: palette.primary,
  },

  table: {
    border: {
      width: 0,
      color: 'transparent',
    },
    header: {
      padding: {
        small: '16px 12px',
      },
    },
    head: {
      backgroundColor: 'transparent',
      borderBottom: `1px solid ${palette.lightBorder}`,
    },
  },

  text: {
    color: {
      primary: palette.primary,
    },
    variant: {
      caption: {
        size: {
          small: '10px',
        },
      },
    },
  },

  textfield: {
    borderRadius: `${borderRadiusBase}px`,
    borderWidth: '1px',
    label: {
      color: palette.dark,
    },
    variant: {
      outlined: {
        borderRadius: `${borderRadiusBase}px`,
        backgroundColor: palette.white,
        borderColor: palette.lightBorder,
        active: {
          borderColor: palette.primary,
        },
      },
      outlinedFilled: {
        borderRadius: `${borderRadiusBase}px`,
        backgroundColor: palette.white,
        borderColor: palette.lightBorder,
        active: {
          borderColor: palette.primary,
          backgroundColor: palette.white,
        },
      },
    },
  },
  textarea: {
    borderRadius: `${borderRadiusBase}px`,
    backgroundColor: palette.white,
    borderWidth: '1px',
    variant: {
      outlined: {
        borderRadius: `${borderRadiusBase}px`,
        backgroundColor: palette.white,
        borderColor: palette.lightBorder,
        active: {
          backgroundColor: palette.white,
        },
      },
      outlinedFilled: {
        borderRadius: `${borderRadiusBase}px`,
        backgroundColor: palette.white,
        borderColor: palette.lightBorder,
        active: {
          borderColor: palette.primary,
          backgroundColor: palette.white,
        },
      },
    },
  },

  timePicker: {
    hoverBackgroundColor: `${palette.dark}10`,
    timeButtonFontSize: '14px',
  },

  select: {
    borderRadius: `${borderRadiusBase}px`,
    border: {
      primary: palette.primary,
    },
    label: {
      color: palette.dark,
    },
    disabled: {
      opacity: 0.75,
    },
  },
  progressIndicator: {
    indicatorColor: palette.primary,
  },
  space: {
    xs: '8px',
    sm: '12px',
    md: '16px',
    lg: '24px',
  },
};
